/**
 * useQuickStart Hook
 *
 * Manages state and logic for the Quick Start onboarding flow.
 * Handles checking status, screen navigation, and completion tracking.
 */

import { useState, useCallback, useEffect, useRef } from "react";
import * as quickStartService from "../services/quickStartService";
import { authService } from "../services/auth";
import logger from "../utils/logger";

const STORAGE_KEY = "quick_start_state";
const SESSION_SHOWN_KEY = "quick_start_shown_this_session";

/**
 * Check if Quick Start was already shown this session (page reload detection)
 * @returns {boolean}
 */
function wasShownThisSession() {
  try {
    const value = sessionStorage.getItem(SESSION_SHOWN_KEY);
    // Handle both old "true" format and new timestamp format
    return value !== null && value !== "";
  } catch {
    return false;
  }
}

/**
 * Mark Quick Start as shown for this session
 */
function markShownThisSession() {
  try {
    const timestamp = new Date().toISOString();
    sessionStorage.setItem(SESSION_SHOWN_KEY, timestamp);
  } catch {
    // Ignore storage errors
  }
}

/**
 * Clear the session shown flag (for logout scenarios)
 */
function clearSessionShown() {
  try {
    sessionStorage.removeItem(SESSION_SHOWN_KEY);
  } catch {
    // Ignore storage errors
  }
}

/**
 * Get initial state from sessionStorage (for resume support)
 * @returns {object|null}
 */
function getStoredState() {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

/**
 * Save state to sessionStorage
 * @param {object} state
 */
function storeState(state) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Ignore storage errors
  }
}

/**
 * Clear stored state
 */
function clearStoredState() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore storage errors
  }
}

export function useQuickStart(user) {
  // Modal visibility
  const [isOpen, setIsOpen] = useState(false);

  // Status data from API
  const [status, setStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Current screen (1, 2, or 3)
  const [currentScreen, setCurrentScreen] = useState(1);

  // Track which screens have been completed
  const [completedScreens, setCompletedScreens] = useState([]);

  // Screen 1: BGG Import state
  const [bggId, setBggId] = useState("");
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importResult, setImportResult] = useState(null);

  // Screen 1: Username state
  const [username, setUsername] = useState("");
  const [isSavingUsername, setIsSavingUsername] = useState(false);
  const [usernameError, setUsernameError] = useState(null);

  // Screen 2: Collection add state
  const [selectedGames, setSelectedGames] = useState([]);

  // Screen 3: Session logging state
  const [sessionOption, setSessionOption] = useState(null); // 'collection_dates' or 'manual'
  const [sessionGames, setSessionGames] = useState([]);

  // Prevent duplicate fetches
  const hasCheckedRef = useRef(false);

  // Reset check flag on mount/unmount for clean state
  useEffect(() => {
    return () => {
      hasCheckedRef.current = false;
    };
  }, []);

  /**
   * Check if Quick Start is needed and open modal if so
   */
  const checkAndShow = useCallback(async () => {
    // Prevent duplicate checks
    if (hasCheckedRef.current) {
      return;
    }

    // Check if already shown this session (page reload detection)
    // Allow if user has partial progress to resume
    const stored = getStoredState();
    const alreadyShown = wasShownThisSession();
    const hasPartialProgress = stored && !stored.completedScreens?.includes(3);

    if (alreadyShown && !hasPartialProgress) {
      hasCheckedRef.current = true;
      return;
    }

    hasCheckedRef.current = true;

    setIsLoading(true);
    setError(null);

    try {
      const quickStartStatus = await quickStartService.getQuickStartStatus();
      setStatus(quickStartStatus);

      if (quickStartStatus.needs_quick_start) {
        // Determine starting screen
        let startScreen = 1;
        if (stored?.currentScreen && quickStartStatus.screens_needed.includes(stored.currentScreen)) {
          startScreen = stored.currentScreen;
        } else if (quickStartStatus.screens_needed.length > 0) {
          startScreen = quickStartStatus.screens_needed[0];
        }

        setCurrentScreen(startScreen);
        setCompletedScreens(stored?.completedScreens || []);

        // Restore screen-specific state
        if (stored?.bggId) setBggId(stored.bggId);
        if (stored?.selectedGames) setSelectedGames(stored.selectedGames);
        if (stored?.sessionOption) setSessionOption(stored.sessionOption);
        if (stored?.sessionGames) setSessionGames(stored.sessionGames);
        setUsername(stored?.username ?? user?.username ?? "");

        setIsOpen(true);
        markShownThisSession(); // Mark as shown for this session
      }
    } catch (err) {
      logger.error("[useQuickStart] Failed to check quick start status:", err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const launch = useCallback(async () => {
    clearStoredState();
    clearSessionShown();
    hasCheckedRef.current = true;
    setIsOpen(false);
    setIsLoading(true);
    setError(null);

    try {
      const quickStartStatus = await quickStartService.getQuickStartStatus();
      setStatus({
        ...quickStartStatus,
        needs_quick_start: true,
        screens_needed: [1, 2, 3],
      });
      setCurrentScreen(1);
      setCompletedScreens([]);
      setBggId("");
      setIsImporting(false);
      setImportProgress(0);
      setImportResult(null);
      setUsername(user?.username || "");
      setUsernameError(null);
      setSelectedGames([]);
      setSessionOption(null);
      setSessionGames([]);
      setIsOpen(true);
      markShownThisSession();
    } catch (err) {
      logger.error("[useQuickStart] Failed to launch quick start:", err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  /**
   * Reset the check flag (for re-authentication scenarios)
   */
  const resetCheck = useCallback(() => {
    hasCheckedRef.current = false;
  }, []);

  /**
   * Force a recheck (for debugging or manual trigger)
   */
  const forceRecheck = useCallback(() => {
    hasCheckedRef.current = false;
    return checkAndShow();
  }, [checkAndShow]);

  /**
   * Persist current state to sessionStorage
   */
  useEffect(() => {
    if (isOpen) {
      storeState({
        currentScreen,
        completedScreens,
        bggId,
        username,
        selectedGames,
        sessionOption,
        sessionGames,
      });
    }
  }, [isOpen, currentScreen, completedScreens, bggId, username, selectedGames, sessionOption, sessionGames]);

  /**
   * Close modal and mark as completed
   */
  const close = useCallback(async () => {
    setIsOpen(false);
    clearStoredState();

    // Mark as completed in backend
    try {
      await quickStartService.markQuickStartCompleted();
    } catch (err) {
      logger.error("[useQuickStart] Failed to mark completion:", err);
    }
  }, []);

  /**
   * Skip current screen and go to next
   */
  const skipScreen = useCallback(() => {
    const remainingScreens = status?.screens_needed?.filter(
      s => s > currentScreen && !completedScreens.includes(s)
    ) || [];

    if (remainingScreens.length > 0) {
      setCurrentScreen(remainingScreens[0]);
    } else {
      close();
    }
  }, [currentScreen, completedScreens, status, close]);

  /**
   * Advance to next screen or close if done
   */
  const nextScreen = useCallback(() => {
    setCompletedScreens(prev => [...prev, currentScreen]);

    const remainingScreens = status?.screens_needed?.filter(
      s => s > currentScreen && !completedScreens.includes(s)
    ) || [];

    if (remainingScreens.length > 0) {
      setCurrentScreen(remainingScreens[0]);
    } else {
      close();
    }
  }, [currentScreen, completedScreens, status, close]);

  /**
   * Go to previous screen (including skipped ones)
   */
  const prevScreen = useCallback(() => {
    // All Quick Start screens are 1, 2, 3
    // Go back to the immediate previous screen number regardless of whether it was skipped
    if (currentScreen > 1) {
      setCurrentScreen(currentScreen - 1);
    }
  }, [currentScreen]);

  /**
   * Screen 1: Handle BGG ID update and import
   */
  const handleBggIdSubmit = useCallback(async (bggIdValue) => {
    setIsImporting(true);
    setImportProgress(0);
    setImportResult(null);

    // Simulate progress during import (backend doesn't provide real-time progress)
    const progressInterval = setInterval(() => {
      setImportProgress(prev => {
        if (prev >= 90) return prev; // Hold at 90% until complete
        return prev + Math.random() * 15; // Increment by 0-15%
      });
    }, 500);

    try {
      // Update BGG ID
      await quickStartService.updateBggId(bggIdValue);
      setImportProgress(30);

      // Import collection
      const result = await quickStartService.importBggCollection();
      setImportProgress(100);
      setImportResult({ success: true, ...result });

      // Wait a moment to show 100% progress
      await new Promise(resolve => setTimeout(resolve, 500));

      // Check if we should skip Step 2 based on new collection count
      const newStatus = await quickStartService.getQuickStartStatus();
      logger.info("[QuickStart] Post-import status:", newStatus);

      // Update local status so nextScreen knows which screens are needed
      setStatus(newStatus);

      clearInterval(progressInterval);
      setIsImporting(false);

      // Determine next screen based on collection count
      if (newStatus.collection_count >= 20 && !newStatus.screens_needed.includes(2)) {
        logger.info("[QuickStart] Collection >= 20 after import, skipping Step 2");
        // Mark screen 1 as completed, set screen to 3 (or next needed)
        setCompletedScreens(prev => [...new Set([...prev, 1])]);
        const nextNeeded = newStatus.screens_needed.find(s => s > 1);
        if (nextNeeded) {
          setCurrentScreen(nextNeeded);
        } else {
          close();
        }
      } else {
        logger.info("[QuickStart] Collection < 20 after import, showing Step 2");
        setCompletedScreens(prev => [...new Set([...prev, 1])]);
        setCurrentScreen(2);
      }
    } catch (err) {
      clearInterval(progressInterval);
      logger.error("[useQuickStart] BGG import failed:", err);
      setImportResult({ success: false, error: err.message });
      setIsImporting(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Screen 1: Handle "I don't have a BGG ID"
   */
  const handleBggIdOptOut = useCallback(async () => {
    try {
      await quickStartService.markBggIdOptOut();
      nextScreen(); // Go to screen 2 for manual add
    } catch (err) {
      logger.error("[useQuickStart] Failed to mark BGG opt-out:", err);
      // Continue anyway
      nextScreen();
    }
  }, [nextScreen]);

  /**
   * Screen 1: Save updated username
   * Returns true on success, false on failure (error is exposed via usernameError)
   */
  const handleUsernameSave = useCallback(async (value) => {
    const trimmed = (value || "").trim();
    if (!trimmed || trimmed === (user?.username || "")) {
      return false;
    }

    setIsSavingUsername(true);
    setUsernameError(null);

    try {
      await authService.updateUsername(trimmed);
      return true;
    } catch (err) {
      logger.error("[useQuickStart] Failed to update username:", err);
      setUsernameError(err.message);
      return false;
    } finally {
      setIsSavingUsername(false);
    }
  }, [user]);

  /**
   * Screen 2: Add game to selection list
   *
   * Default state mirrors AddGameModal's fresh-add default: no shelf status
   * selected, has_played=true (matches the no_shelf catch-all + has_played
   * constraint enforced by the backend).
   */
  const addGameToSelection = useCallback((game, ownershipStatus = null, preference = "neutral") => {
    setSelectedGames(prev => {
      if (prev.find(g => g.id === game.id)) return prev;
      const hasPlayed = ownershipStatus ? false : true;
      return [...prev, { ...game, ownershipStatus, hasPlayed, preference }];
    });
  }, []);

  /**
   * Screen 2: Update game ownership status (shelf category)
   *
   * Clicking the currently-active shelf button deselects it. has_played is
   * independent of ownership_status, but clearing the shelf while has_played
   * is false would violate the no_shelf-requires-played constraint, so
   * has_played is auto-forced to true in that case (mirrors the backend rule).
   */
  const updateGameOwnership = useCallback((gameId, ownershipStatus) => {
    setSelectedGames(prev =>
      prev.map(g => {
        if (g.id !== gameId) return g;
        const nextOwnershipStatus = g.ownershipStatus === ownershipStatus ? null : ownershipStatus;
        const nextHasPlayed = (!nextOwnershipStatus && !g.hasPlayed) ? true : g.hasPlayed;
        return { ...g, ownershipStatus: nextOwnershipStatus, hasPlayed: nextHasPlayed };
      })
    );
  }, []);

  /**
   * Screen 2: Update game has_played, independent of ownership_status.
   *
   * Blocks turning has_played off when there's no shelf status selected,
   * since that combination is forbidden by the backend constraint.
   */
  const updateGameHasPlayed = useCallback((gameId, hasPlayed) => {
    setSelectedGames(prev =>
      prev.map(g => {
        if (g.id !== gameId) return g;
        if (!hasPlayed && !g.ownershipStatus) return g;
        return { ...g, hasPlayed };
      })
    );
  }, []);

  /**
   * Screen 2: Update game preference
   */
  const updateGamePreference = useCallback((gameId, preference) => {
    setSelectedGames(prev =>
      prev.map(g => g.id === gameId ? { ...g, preference } : g)
    );
  }, []);

  /**
   * Screen 2: Remove game from selection
   */
  const removeGameFromSelection = useCallback((gameId) => {
    setSelectedGames(prev => prev.filter(g => g.id !== gameId));
  }, []);

  /**
   * Screen 2: Bulk add all selected games to collection
   */
  const bulkAddToCollection = useCallback(async () => {
    setIsLoading(true);

    try {
      const results = await Promise.all(
        selectedGames.map(game =>
          quickStartService.addToCollection({
            game_id: game.id,
            ownership_status: game.ownershipStatus || "no_shelf",
            has_played: game.hasPlayed,
            preference: game.preference,
          })
        )
      );

      logger.info("[useQuickStart] Bulk added games:", results.length);
      nextScreen();
    } catch (err) {
      logger.error("[useQuickStart] Failed to bulk add games:", err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [selectedGames, nextScreen]);

  /**
   * Screen 3: Add game to session list
   */
  const addGameToSession = useCallback((game, result = "loss") => {
    setSessionGames(prev => {
      if (prev.find(g => g.id === game.id)) return prev;
      return [...prev, { ...game, result }];
    });
  }, []);

  /**
   * Screen 3: Update game result (win/loss)
   */
  const updateGameResult = useCallback((gameId, result) => {
    setSessionGames(prev =>
      prev.map(g => g.id === gameId ? { ...g, result } : g)
    );
  }, []);

  /**
   * Screen 3: Remove game from session list
   */
  const removeGameFromSession = useCallback((gameId) => {
    setSessionGames(prev => prev.filter(g => g.id !== gameId));
  }, []);

  /**
   * Screen 3: Create sessions from collection dates
   * Uses batch endpoint: first N created synchronously, rest in background
   */
  const createSessionsFromCollection = useCallback(async () => {
    setIsLoading(true);

    try {
      logger.info("[useQuickStart] Creating sessions from collection via batch");

      // Get collection games (already loaded in state from Step 2, or fetch if needed)
      const collectionGames = selectedGames.length > 0 ? selectedGames : await quickStartService.getCollectionGames();
      
      if (!collectionGames || collectionGames.length === 0) {
        setError("No games in collection to create sessions from");
        return;
      }

      // Use all collection game IDs
      const gameIds = collectionGames.map(g => g.game_id || g.id).filter(Boolean);
      
      if (gameIds.length === 0) {
        setError("No valid game IDs found");
        return;
      }

      logger.info(`[useQuickStart] Batch creating sessions for ${gameIds.length} games`);

      // Call batch endpoint - creates first 20 sync, rest async
      const result = await quickStartService.batchCreateSessions({
        game_ids: gameIds,
        batch_size: 20,
      });

      logger.info("[useQuickStart] Batch result:", result);

      if (result.task_id) {
        // Background task started - show info to user
        const remaining = result.total_count - result.created_count;
        logger.info(`[useQuickStart] ${result.created_count} sessions created now, ${remaining} in background (task: ${result.task_id})`);
        
        // Poll for completion in background
        pollBatchTaskCompletion(result.task_id);
      }

      close();
    } catch (err) {
      logger.error("[useQuickStart] Failed to create sessions from collection:", err);
      setError(err.message || "Failed to create sessions");
    } finally {
      setIsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [close, selectedGames]);

  /**
   * Poll for batch task completion and notify user
   */
  const pollBatchTaskCompletion = useCallback(async (taskId) => {
    const checkInterval = 5000; // 5 seconds
    const maxAttempts = 60; // 5 minutes max
    let attempts = 0;

    const checkStatus = async () => {
      try {
        const status = await quickStartService.getBatchSessionStatus(taskId);
        attempts++;

        logger.info(`[QuickStart] Batch task ${taskId} status:`, status);

        if (status.status === "completed") {
          // Show completion notification
          const message = `Created ${status.created} more sessions from your collection.`;
          logger.info(`[QuickStart] ${message}`);
          
          // Use browser notification if available
          if ("Notification" in window && Notification.permission === "granted") {
            new Notification("Pista - Sessions Created", {
              body: message,
              icon: "/favicon.ico",
            });
          } else {
            // Fallback to simple alert or toast
            window.alert(message);
          }
          return;
        }

        if (status.status === "failed") {
          logger.error(`[QuickStart] Batch task ${taskId} failed:`, status.error);
          return;
        }

        if (attempts < maxAttempts) {
          setTimeout(checkStatus, checkInterval);
        } else {
          logger.warn(`[QuickStart] Batch task ${taskId} polling timed out`);
        }
      } catch (err) {
        logger.error(`[QuickStart] Failed to check batch status for ${taskId}:`, err);
      }
    };

    // Start polling after a short delay
    setTimeout(checkStatus, checkInterval);
  }, []);

  /**
   * Screen 3: Bulk create manual sessions
   */
  const bulkCreateSessions = useCallback(async () => {
    setIsLoading(true);

    try {
      const today = new Date().toISOString();

      await Promise.all(
        sessionGames.map(game =>
          quickStartService.createSimplifySession({
            chosen_game_id: game.id,
            chosen_at: today,
            winners: game.result === "win" ? [status?.user_id || 0] : [0], // 0 = system winner
          })
        )
      );

      logger.info("[useQuickStart] Created sessions:", sessionGames.length);
      close();
    } catch (err) {
      logger.error("[useQuickStart] Failed to create sessions:", err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [sessionGames, status, close]);

  /**
   * Clear all state (for testing/reset/logout)
   */
  const reset = useCallback(() => {
    setIsOpen(false);
    setStatus(null);
    setCurrentScreen(1);
    setCompletedScreens([]);
    setBggId("");
    setIsImporting(false);
    setImportProgress(0);
    setImportResult(null);
    setUsername("");
    setUsernameError(null);
    setSelectedGames([]);
    setSessionOption(null);
    setSessionGames([]);
    setError(null);
    clearStoredState();
    clearSessionShown(); // Clear session flag for logout scenarios
    hasCheckedRef.current = false;
  }, []);

  return {
    // Modal state
    isOpen,
    isLoading,
    error,
    status,
    currentScreen,
    completedScreens,

    // Screen 1 state
    bggId,
    setBggId,
    isImporting,
    importProgress,
    importResult,
    username,
    setUsername,
    isSavingUsername,
    usernameError,

    // Screen 2 state
    selectedGames,

    // Screen 3 state
    sessionOption,
    setSessionOption,
    sessionGames,

    // Actions
    checkAndShow,
    launch,
    resetCheck,
    forceRecheck,
    close,
    skipScreen,
    nextScreen,
    prevScreen,
    reset,

    // Screen 1 actions
    handleBggIdSubmit,
    handleBggIdOptOut,
    handleUsernameSave,

    // Screen 2 actions
    addGameToSelection,
    updateGameOwnership,
    updateGameHasPlayed,
    updateGamePreference,
    removeGameFromSelection,
    bulkAddToCollection,

    // Screen 3 actions
    addGameToSession,
    updateGameResult,
    removeGameFromSession,
    createSessionsFromCollection,
    bulkCreateSessions,
  };
}

export default useQuickStart;
